import React, { useState, type FormEvent } from 'react'
import { useParams, useSearchParams } from 'react-router'
import {
  AppLayout,
  CheckIcon,
  FetchError,
  formatAnchorLabel,
  formatCandidateLabel,
  formatTierLabel,
  getRankingCompletenessMessage,
  getSampleLookupErrorMessage,
  getSampleStatus,
  MagnifyingGlassIcon,
  ModalDialog,
  Mtds,
  resolveCurrentInspectorDisplay,
  Sample,
  SampleInspectorCandidate,
  SampleInspectorCandidates,
  SampleStatusTag,
  SaveError,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  useGetSampleByIdQuery,
  useGetSampleInspectorCandidatesQuery,
  useUpdateSampleInspectorMutation,
  WarningIcon,
  XIcon
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'

type SampleLookupCardProps = {
  sampleIdInput: string
  setSampleIdInput: (value: string) => void
  onLookup: (e: FormEvent<HTMLFormElement>) => void
  isLoading: boolean
}

const SampleLookupCard = ({
  sampleIdInput,
  setSampleIdInput,
  onLookup,
  isLoading
}: SampleLookupCardProps) => (
  <Mtds.Card data-pad='4'>
    <form onSubmit={onLookup}>
      <Mtds.Flex data-gap='4' data-align='end'>
        <Mtds.Field
          as='input'
          id='sample-id-input'
          label='Prøve-ID'
          name='sampleId'
          placeholder='Skriv inn nøyaktig prøve-ID (f.eks. 1)'
          value={sampleIdInput}
          onChange={(e) => setSampleIdInput(e.currentTarget.value)}
          required
        />
        <Mtds.Button
          type='submit'
          data-variant='primary'
          disabled={isLoading || !sampleIdInput.trim()}
          aria-busy={isLoading}
        >
          <MagnifyingGlassIcon />
          Hent prøve
        </Mtds.Button>
      </Mtds.Flex>
    </form>
  </Mtds.Card>
)

type SampleDetailsCardProps = {
  sample: Sample
  candidatesData?: SampleInspectorCandidates
}

const SampleDetailsCard = ({
  sample,
  candidatesData
}: SampleDetailsCardProps) => {
  const currentInspectorText = resolveCurrentInspectorDisplay(
    sample.userId,
    candidatesData?.candidates,
    candidatesData?.currentInspector
  )

  const anchorText = formatAnchorLabel(candidatesData?.anchor)
  const taskText =
    sample.taskId != null ? `Oppgave #${sample.taskId}` : 'Ingen oppgave'

  return (
    <Mtds.Card data-pad='5'>
      <Mtds.Prose>
        <Mtds.Heading as='h2' data-size='md'>
          Prøvedetaljer
        </Mtds.Heading>
        <Mtds.Grid data-items='250' data-gap='4'>
          <Mtds.Info>
            <strong>Prøvestatus</strong>
            <SampleStatusTag status={getSampleStatus(sample)} />
          </Mtds.Info>
          <Mtds.Info>
            <strong>Oppgave</strong>
            {taskText}
          </Mtds.Info>
          <Mtds.Info>
            <strong>Tilknyttet organisasjon</strong>
            {anchorText}
          </Mtds.Info>
          <Mtds.Info>
            <strong>Nåværende inspektør</strong>
            {currentInspectorText}
          </Mtds.Info>
        </Mtds.Grid>
      </Mtds.Prose>
    </Mtds.Card>
  )
}

type InspectorSelectCardProps = {
  candidates: SampleInspectorCandidate[]
  selectedCandidate: SampleInspectorCandidate | null
  currentInspectorUserId?: string
  onSelectCandidate: (candidate: SampleInspectorCandidate | null) => void
  onOpenConfirm: () => void
  isPending: boolean
  hasConflict: boolean
}

const InspectorSelectCard = ({
  candidates,
  selectedCandidate,
  currentInspectorUserId,
  onSelectCandidate,
  onOpenConfirm,
  isPending,
  hasConflict
}: InspectorSelectCardProps) => {
  const suggestionOptions = candidates.map((candidate) => {
    const label = formatCandidateLabel(candidate)
    const tier = formatTierLabel(candidate.tier)
    return {
      value: candidate.userId,
      label: `${label} (${tier})`
    }
  })

  const selectedSuggestion = selectedCandidate
    ? [
        {
          value: selectedCandidate.userId,
          label: `${formatCandidateLabel(selectedCandidate)} (${formatTierLabel(selectedCandidate.tier)})`
        }
      ]
    : []

  const isCurrent = Boolean(
    selectedCandidate &&
    currentInspectorUserId &&
    selectedCandidate.userId === currentInspectorUserId
  )

  const canSave =
    selectedCandidate != null && !isCurrent && !isPending && !hasConflict

  return (
    <Mtds.Card data-pad='5'>
      <Mtds.Prose>
        <Mtds.Heading as='h2' data-size='md'>
          Velg ny inspektør
        </Mtds.Heading>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (canSave) onOpenConfirm()
          }}
        >
          <Mtds.Flex data-gap='4'>
            <Mtds.Field label='Inspektør'>
              <Mtds.Field.Suggestion
                selected={selectedSuggestion}
                onSelectedChange={(items) => {
                  const item = items?.[0]
                  const found = candidates.find((c) => c.userId === item?.value)
                  onSelectCandidate(found ?? null)
                }}
              >
                <Mtds.Input
                  id='inspector-select-input'
                  placeholder='Søk etter navn eller e-post'
                />
                <del aria-label='Fjern tekst'></del>
                <Mtds.Field.Datalist>
                  {suggestionOptions.map((opt) => (
                    <Mtds.Field.Option
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                    >
                      {opt.label}
                    </Mtds.Field.Option>
                  ))}
                </Mtds.Field.Datalist>
              </Mtds.Field.Suggestion>
            </Mtds.Field>

            {selectedCandidate && (
              <Mtds.Info>
                <strong>Valgt inspektør:</strong>
                {formatCandidateLabel(selectedCandidate)} (
                {formatTierLabel(selectedCandidate.tier)})
              </Mtds.Info>
            )}

            {isCurrent && (
              <Mtds.Alert data-color='info'>
                <p>Valgt bruker er allerede inspektør for denne prøven.</p>
              </Mtds.Alert>
            )}

            <Mtds.Flex data-justify='start'>
              <Mtds.Button
                type='submit'
                data-variant='primary'
                disabled={!canSave}
                aria-busy={isPending}
              >
                <CheckIcon />
                Lagre inspektør
              </Mtds.Button>
            </Mtds.Flex>
          </Mtds.Flex>
        </form>
      </Mtds.Prose>
    </Mtds.Card>
  )
}

type ConfirmInspectorModalProps = {
  isOpen: boolean
  sampleId: string
  currentInspectorText: string
  newInspectorText: string
  onClose: () => void
  onConfirm: () => void
  isPending: boolean
  hasConflict: boolean
  error: unknown
}

const ConfirmInspectorModal = ({
  isOpen,
  sampleId,
  currentInspectorText,
  newInspectorText,
  onClose,
  onConfirm,
  isPending,
  hasConflict,
  error
}: ConfirmInspectorModalProps) => {
  const modalId = 'confirm-inspector-change-modal'
  return (
    <ModalDialog
      id={modalId}
      closedby='any'
      open={isOpen}
      onClose={onClose}
      aria-labelledby={`${modalId}-title`}
    >
      <Mtds.Button command='close' commandfor={modalId} aria-label='Lukk' />
      <Mtds.Prose>
        <Mtds.Heading as='h2' id={`${modalId}-title`}>
          Bekreft endring av inspektør
        </Mtds.Heading>
        <SaveError error={error} action='lagre inspektøren' />
        <p>
          Er du sikker på at du vil endre inspektør for prøve{' '}
          <strong>{sampleId}</strong>?
        </p>

        <Mtds.Card data-pad='4'>
          <Mtds.Grid data-gap='3'>
            <Mtds.Info>
              <strong>Nåværende inspektør</strong>
              {currentInspectorText}
            </Mtds.Info>
            <Mtds.Info>
              <strong>Ny inspektør</strong>
              {newInspectorText}
            </Mtds.Info>
          </Mtds.Grid>
        </Mtds.Card>

        <Mtds.Alert data-color='warning'>
          <p>
            <strong>Merk:</strong> Bare eierskapet til prøven endres. Tilhørende
            dokumenter og arkiverte journalposter endres ikke.
          </p>
        </Mtds.Alert>

        <Mtds.Flex data-gap='4' data-justify='end'>
          <Mtds.Button
            command='close'
            commandfor={modalId}
            disabled={isPending}
          >
            <XIcon />
            Avbryt
          </Mtds.Button>
          <Mtds.Button
            data-variant='primary'
            disabled={isPending || hasConflict}
            aria-busy={isPending}
            onClick={onConfirm}
          >
            <CheckIcon />
            Bekreft endring
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Prose>
    </ModalDialog>
  )
}

export const EditSampleScreen = () => {
  const params = useParams<{ sampleId?: string }>()
  const [searchParams] = useSearchParams()
  const initialId = params.sampleId ?? searchParams.get('sampleId') ?? ''

  const [sampleIdInput, setSampleIdInput] = useState(initialId)
  const [searchedSampleId, setSearchedSampleId] = useState(initialId)
  const [selectedCandidate, setSelectedCandidate] =
    useState<SampleInspectorCandidate | null>(null)
  const [capturedBaseEtag, setCapturedBaseEtag] = useState<string | null>(null)
  const [isConfirmOpen, setConfirmOpen] = useState(false)
  const [isRefreshingConflict, setIsRefreshingConflict] = useState(false)
  const [saveError, setSaveError] = useState<unknown>(null)

  const {
    data: sample,
    isLoading: isLoadingSample,
    isError: isSampleError,
    error: sampleError,
    refetch: refetchSample
  } = useGetSampleByIdQuery(searchedSampleId)

  const {
    data: candidatesData,
    isLoading: isLoadingCandidates,
    isError: isCandidatesError,
    refetch: refetchCandidates
  } = useGetSampleInspectorCandidatesQuery(searchedSampleId)

  const updateInspectorMutation = useUpdateSampleInspectorMutation()

  // Keep the draft visible, but never save it against a silently refreshed ETag.
  const hasEtagConflict = Boolean(
    sample &&
    selectedCandidate &&
    capturedBaseEtag &&
    sample.etag !== capturedBaseEtag
  )

  const effectiveSelectedCandidate = selectedCandidate

  const activeConflictMessage = hasEtagConflict
    ? 'Prøven ble oppdatert i bakgrunnen med nye data mens du valgte inspektør. Valget ditt er beholdt. Noter valget før du henter oppdaterte data og velger inspektør på nytt.'
    : null

  const handleLookup = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const trimmed = sampleIdInput.trim()
    if (!trimmed) return
    if (trimmed === searchedSampleId) {
      refetchSample()
      refetchCandidates()
    } else {
      setSearchedSampleId(trimmed)
    }
    setSelectedCandidate(null)
    setCapturedBaseEtag(null)
    setSaveError(null)
    setConfirmOpen(false)
  }

  const handleSelectCandidate = (
    candidate: SampleInspectorCandidate | null
  ) => {
    setSelectedCandidate(candidate)
    setCapturedBaseEtag(sample?.etag ?? null)
    setSaveError(null)
  }

  const handleOpenConfirm = () => {
    if (!sample || !effectiveSelectedCandidate || !capturedBaseEtag) return
    if (hasEtagConflict) return
    setConfirmOpen(true)
  }

  const handleConfirmSave = async () => {
    if (
      !sample ||
      !effectiveSelectedCandidate ||
      !capturedBaseEtag ||
      hasEtagConflict
    )
      return

    setSaveError(null)
    try {
      await updateInspectorMutation.mutateAsync({
        sampleId: sample.id,
        etag: capturedBaseEtag,
        userId: effectiveSelectedCandidate.userId
      })
      Mtds.toast.success('Inspektør ble oppdatert')
      setConfirmOpen(false)
      setSelectedCandidate(null)
      setCapturedBaseEtag(null)
      setSaveError(null)
    } catch (error) {
      setSaveError(error)
    }
  }

  // Use authoritative sample.userId for current owner snapshot
  const currentInspectorSummary = resolveCurrentInspectorDisplay(
    sample?.userId,
    candidatesData?.candidates,
    candidatesData?.currentInspector
  )

  const newInspectorSummary = effectiveSelectedCandidate
    ? `${formatCandidateLabel(effectiveSelectedCandidate)} (${formatTierLabel(effectiveSelectedCandidate.tier)})`
    : ''

  const completenessWarning = getRankingCompletenessMessage(
    candidatesData?.rankingCompleteness
  )

  const sampleErrorDetails = getSampleLookupErrorMessage(
    sampleError,
    searchedSampleId
  )

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      topBar={<TopBar logo={<AppLogo />} title='Rediger prøve' />}
    >
      <ScreenLayout
        header={
          <ScreenHeader
            title='Rediger prøve'
            description={
              <p>
                Søk opp en prøve med prøve-ID for å administrere og endre
                tildelt inspektør, uavhengig av prøvens status.
              </p>
            }
          />
        }
      >
        <SampleLookupCard
          sampleIdInput={sampleIdInput}
          setSampleIdInput={setSampleIdInput}
          onLookup={handleLookup}
          isLoading={isLoadingSample}
        />

        {isLoadingSample && (
          <Mtds.Card data-pad='4'>
            <Mtds.Flex data-align='center' data-gap='3'>
              <Mtds.Spinner />
              <p>Laster prøveinformasjon...</p>
            </Mtds.Flex>
          </Mtds.Card>
        )}

        {isSampleError && searchedSampleId && (
          <Mtds.Alert data-color='danger'>
            <Mtds.Heading as='h3' data-size='sm'>
              {sampleErrorDetails.title}
            </Mtds.Heading>
            <p>{sampleErrorDetails.message}</p>
            <Mtds.Button
              data-size='sm'
              onClick={() => {
                refetchSample()
                refetchCandidates()
              }}
            >
              Prøv igjen
            </Mtds.Button>
          </Mtds.Alert>
        )}

        {sample && (
          <>
            <SampleDetailsCard
              sample={sample}
              candidatesData={candidatesData}
            />

            {isLoadingCandidates && (
              <Mtds.Card data-pad='4'>
                <Mtds.Flex data-align='center' data-gap='3'>
                  <Mtds.Spinner />
                  <p>Laster inspektørkandidater...</p>
                </Mtds.Flex>
              </Mtds.Card>
            )}

            {isCandidatesError && (
              <Mtds.Alert data-color='danger'>
                <Mtds.Heading as='h3' data-size='sm'>
                  Kunne ikke hente inspektørkandidater
                </Mtds.Heading>
                <p>
                  Det oppstod en feil ved innhenting av kandidater for denne
                  prøven.
                </p>
                <Mtds.Button data-size='sm' onClick={() => refetchCandidates()}>
                  Prøv igjen
                </Mtds.Button>
              </Mtds.Alert>
            )}

            {completenessWarning && (
              <Mtds.Alert data-color='warning'>
                <Mtds.Heading as='h3' data-size='sm'>
                  <WarningIcon /> Begrenset orgenhetsinformasjon
                </Mtds.Heading>
                <p>{completenessWarning}</p>
              </Mtds.Alert>
            )}

            {activeConflictMessage && (
              <Mtds.Alert data-color='danger' id='conflict-alert'>
                <Mtds.Heading as='h3' data-size='sm'>
                  Konflikt ved lagring
                </Mtds.Heading>
                <p>{activeConflictMessage}</p>
                <Mtds.Button
                  data-size='sm'
                  disabled={isRefreshingConflict}
                  onClick={async () => {
                    setIsRefreshingConflict(true)
                    try {
                      const [freshSample, freshCandidates] = await Promise.all([
                        refetchSample(),
                        refetchCandidates()
                      ])
                      if (freshSample.isSuccess && freshCandidates.isSuccess) {
                        setSelectedCandidate(null)
                        setCapturedBaseEtag(null)
                        setSaveError(null)
                        setConfirmOpen(false)
                      }
                    } finally {
                      setIsRefreshingConflict(false)
                    }
                  }}
                >
                  Hent oppdaterte data
                </Mtds.Button>
              </Mtds.Alert>
            )}

            {candidatesData && candidatesData.candidates.length === 0 && (
              <Mtds.Card data-pad='4'>
                <p>Ingen tilgjengelige inspektørkandidater funnet.</p>
              </Mtds.Card>
            )}

            {candidatesData && candidatesData.candidates.length > 0 && (
              <InspectorSelectCard
                candidates={candidatesData.candidates}
                selectedCandidate={effectiveSelectedCandidate}
                currentInspectorUserId={sample.userId}
                onSelectCandidate={handleSelectCandidate}
                onOpenConfirm={handleOpenConfirm}
                isPending={updateInspectorMutation.isPending}
                hasConflict={hasEtagConflict}
              />
            )}

            {effectiveSelectedCandidate && (
              <ConfirmInspectorModal
                isOpen={isConfirmOpen}
                sampleId={sample.id}
                currentInspectorText={currentInspectorSummary}
                newInspectorText={newInspectorSummary}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmSave}
                isPending={updateInspectorMutation.isPending}
                hasConflict={hasEtagConflict}
                error={
                  saveError ??
                  (hasEtagConflict
                    ? new FetchError('Prøven er endret.', 412)
                    : null)
                }
              />
            )}
          </>
        )}
      </ScreenLayout>
    </AppLayout>
  )
}
